import ko from "knockout";
import _ from "lodash";
import ViewModel from "viewModels/ViewModel";
import loggingService from "services/loggingService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";
/**
 * ViewModel used to switch between mutually exclusive views.
 * When switching views, the old active view is destroyed and
 * a new instance of the type being switched to is created.
 *
 * @exports viewModels/SwitchedView
 */

export default ViewModel.extend(function (viewModels) {
    const self = this;
    const activeView = ko.observable(); // The name of the view that is currently active.
    const viewData = {};

    const isDisposable = (view) => view && _.isFunction(view.dispose);

    const notSelected = (viewName) => activeView() !== viewName;

    const isNotPersisted = (view) => {
        if (view) {
            return _.find(viewData, (meta) => {
                return meta.instance === view && !meta.persist;
            });
        }
    };

    const disposeInactiveViews = (disposePersistedFlag) => {
        Object.keys(viewData)
            .filter(notSelected)
            .forEach((viewName) => {
                const view = self[viewName]();
                if (
                    isDisposable(view) &&
                    (disposePersistedFlag || isNotPersisted(view))
                ) {
                    self.disposeSubView(view);
                }
                self[viewName](undefined);
            });
    };

    // Activates a view by creating a new instance.
    const activateTransientView = (viewName, Constructor, args) => {
        args.unshift(null);
        const ViewFactory = Constructor.bind.apply(Constructor, args);
        const viewInstance = self.subView(ViewFactory);
        viewData[viewName].instance = viewInstance;
        self[viewName](viewInstance);
    };

    // Activates a view by retrieving a singleton instance.
    const activatePersistentView = (viewName, Constructor, args) => {
        const viewInstance = viewData[viewName].instance;
        if (viewInstance) {
            self[viewName](viewInstance);
        } else {
            // If there is no instance then make one.
            activateTransientView(viewName, Constructor, args);
        }
    };

    // Activates a view by its name.
    const activateView = (viewName, args) => {
        if (viewName) {
            const meta = viewData[viewName];
            if (meta.Constructor) {
                args = args.length > 0 ? args : meta.arguments;
                if (meta.persist) {
                    activatePersistentView(viewName, meta.Constructor, args);
                } else {
                    if (viewName === activeView()) {
                        self.disposeView(viewName);
                    }
                    activateTransientView(viewName, meta.Constructor, args);
                }
            }
        }
        activeView(viewName);
        disposeInactiveViews(false);
    };

    /**
     * Returns the name of the currently active view.
     */
    self.getActiveView = self.computed(() => activeView());

    /**
     * Makes a view active, returning an instance of it. This returns an instance of the view.
     */
    self.setActiveView = (viewName, ...viewArgs) => {
        if (self[viewName]) {
            activateView(viewName, viewArgs);
            return self[viewName]();
        } else if (viewName === undefined || viewName === null) {
            activateView(viewName, []);
        } else {
            const message =
                'No sub-view with name "' + viewName + '" was found.';
            loggingService.error(message);
            throw message;
        }
    };

    /**
     * Adds a view that always returns a NEW instance when it becomes active. Chainable.
     */
    self.addView = (viewName, Constructor, ...viewArgs) => {
        viewData[viewName] = {
            Constructor: Constructor,
            instance: null,
            persist: false,
            arguments: viewArgs,
        };
        self[viewName] = ko.observable();

        return self;
    };

    /**
     * Adds a view that always returns the SAME instance when it becomes active.  Chainable.
     */
    self.addPersistentView = (...args) => {
        const [viewName] = args;
        self.addView.apply(this, args);
        viewData[viewName].persist = true;
        return self;
    };

    /**
     * Explicitly invalidates the singleton instance of a persisted view.
     */
    self.invalidateViewInstance = (viewName) => {
        const { instance } = viewData[viewName];
        if (instance && instance.dispose) {
            viewData[viewName].instance.dispose();
            viewData[viewName].instance = null;
        }
    };

    self.onDispose = () => {
        if (activeView()) {
            self.disposeView(activeView());
            activeView(undefined);
        }
        disposeInactiveViews(true);
    };

    self.disposeView = (viewName) => {
        if (self[viewName]) {
            const view = self[viewName]();
            if (isDisposable(view)) {
                self.disposeSubView(view);
            }
            self[viewName](undefined);
        }
    };

    /**
     * This is primarily left for backwards compatibility.
     */
    _.each(viewModels, (Constructor, viewName) => {
        self.addView(viewName, Constructor);
    });
});
